<template>
	<NuxtLayout>
    <div class="contactForm container" v-if="!isFormSuccess">
    	<div class="contactForm__Title">
        <h1>{{ $t('contactForm.title') }}</h1>
        <p>{{ $t('contactForm.lead') }}</p>
      </div>

      <div class="contactForm__Select absoluteLabelsForm">
        <SelectField v-model="selectedCategory" :options="categories" :label="$t('header.selectCategory')" text-key="text" value-key="value" />
      </div>

      <div class="contactForm__Link" v-if="selectedCategory && ['MODIFY_RESERVATION', 'CANCEL_RESERVATION'].includes(selectedCategory)">
        <Button type="nuxt-link" :to="`https://book.aminess.com/book.php?company_id=550bdb327ea4004d8e6521ebd5d2bb24&view_cancel=1&lang=${ lang === 'sl' ? 'si' : lang }`" external medium full-width>
          {{ $t('contactFormCategories.modifyReservation') }}
        </Button>
      </div>

      <div class="contactForm__Faq" v-if="selectedCategory">
        <h2>{{ $t('contactForm.faqTitle') }}</h2>
        <div class="faq__List">
          <FaqItem v-for="item in faqs[selectedCategory]" :item="item" />
        </div>
      </div>

      <template v-if="selectedCategory">
        <template v-if="selectedCategory === 'NEW_RESERVATION'">
          <ContactFormNewReservation :properties="properties" @formSubmitted="formSubmitted"/>
        </template>
        <template v-else-if="selectedCategory === 'MODIFY_RESERVATION'">
          <ContactFormModifyReservation :properties="properties" @formSubmitted="formSubmitted"/>
        </template>
        <template v-else-if="selectedCategory === 'CANCEL_RESERVATION'">
          <ContactFormCancelReservation :properties="properties" @formSubmitted="formSubmitted"/>
        </template>
        <template v-else-if="selectedCategory === 'LOST_ITEMS'">
          <ContactFormLostItems :properties="properties" @formSubmitted="formSubmitted"/>
        </template>
        <template v-else-if="selectedCategory === 'PROPERTY_INFO'">
          <ContactFormPropertyInfo :properties="properties" @formSubmitted="formSubmitted"/>
        </template>
        <template v-else-if="selectedCategory === 'OTHER'">
          <ContactFormOther :properties="properties" @formSubmitted="formSubmitted"/>
        </template>
      </template>

      <div class="contactForm__Additional">
        <h2>{{ $t('contactForm.formAdditional') }}</h2>
        <div class="header__ModalSection">
          <div v-if="contact?.phone_number">
            <h4 v-text="$t('header.phone')" />
            <a :href="'tel:' + contact.phone_number" v-text="contact.phone_number" class="header__ModalPhoneNumber button__Text--Underline" />
            <p v-if="contact.working_hours" v-text="contact.working_hours" class="header__ModalHours" />
          </div>

          <div>
            <h4 v-text="$t('header.help')" />
            <nuxt-link :to="link('FAQ')" class="button__Text--Underline">{{ $t('header.findAnswers') }}</nuxt-link>
          </div>
        </div>
      </div>
    </div>
    <div class="contactForm__Success container" v-else>
      <Icon name="check-circle-fill" />
      <h2 v-text="$t('contactForm.success')" />
      <NuxtLink :to="link('Home')" class="button--Primary">{{ $t('error.missingPageButton') }}</NuxtLink>
    </div>
  </NuxtLayout>
</template>

<script setup>
	defineOptions({
    auth: 'aminess'
  });
	
	defineProps({
		page: {},
	});

  const router = useRouter();
  const route = useRoute();
	const { initialData, accommodationsData, link } = useSetup();
	const { user: userData } = useAuth('aminess');
	const { t } = useI18n();
  const { lang } = useLanguage();
	const contact = computed(() => initialData.value.contact);

	const categories = ref([
    {
      text: t('contactFormCategories.newReservation'),
      value: 'NEW_RESERVATION',
    },
    {
      text: t('contactFormCategories.modifyReservation'),
      value: 'MODIFY_RESERVATION',
    },
    {
      text: t('contactFormCategories.cancelReservation'),
      value: 'CANCEL_RESERVATION',
    },
    {
      text: t('contactFormCategories.lostItems'),
      value: 'LOST_ITEMS',
    },
    {
      text: t('contactFormCategories.propertyInfo'),
      value: 'PROPERTY_INFO',
    },
    {
      text: t('contactFormCategories.other'),
      value: 'OTHER',
    },
  ]);
  const selectedCategory = ref(null);
  const faqs = ref([]);

  const isFormSuccess = ref(false);

  const properties = computed(() => {
    return Object.values(accommodationsData.value).map((item) => ({
     name: item.name,
     id: item.id,
   }));
  });

  if(route?.query?.type) {
    selectedCategory.value = route.query.type;
  }

  try {
  	let response = await useServerFetch('contact-form/faqs/', {
  		method: 'GET',
  	});
  	faqs.value = response;
  }
  catch(e) {
  	console.log(e);
  }

  function formSubmitted() {
    isFormSuccess.value = true;
    nextTick(() => {
      let element = document.querySelector('.header');
      if(element) {
        element.scrollIntoView({
          behavior: 'smooth',
        });
      }
    });
  }

  watch(() => selectedCategory.value, (newValue) => {
    router.replace({
      query: {
        ...route.query,
        type: newValue || undefined,
      },
    });
  });

  watch(() => route, (newValue) => {
    selectedCategory.value = newValue?.query?.type || null;
  }, {
    deep: true,
  });
</script>

<style lang="scss">
	@import url('@/assets/styles/pages/_contactForm.scss');
</style>
